// src/app/components/brand/scope-picker.tsx
//
// Brand Scope Picker: anchored combobox popover that drops from the homepage
// search pill. Built to .night/specs/DESIGN_scope_picker_handoff.md, extended
// with entity disambiguation and a chained page flow (operator decision 7/17:
// ask, never assume).
//
// Page flow:
//   root (entity mode)  : "Which Apple do you mean?" Same-name organizations.
//                         Picking the PRIMARY entity (the one the categories
//                         and products belong to) chains to the scope page
//                         with its anchor attached; other entities (Apple
//                         Bank) confirm immediately.
//   root / scope page   : "Which part of Apple?" All-of row + category rows +
//                         "A specific product" drill. Every confirm carries
//                         the pending entity anchor when one exists.
//   products            : product candidates, filterable, Back pops up a level.
//
// Rendered into a document.body portal positioned off the pill so no ancestor
// overflow can clip it.
//
// Copy rule (hard): never show the words "entity", "resolution", "scope", or
// "taxonomy" to the user. No em dashes anywhere.

import type React from "react";
import {
  forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState,
} from "react";
import { createPortal } from "react-dom";
import { Search, CornerDownLeft, ChevronRight, ChevronLeft } from "lucide-react";

export interface ResolvedCandidate {
  name: string;
  kind: "category" | "product" | "entity";
  confidence?: number;
  location?: string;
  domain?: string;
  descriptor?: string;
  /** True when this is the fuzzy-matched onboarded client from intake. */
  known?: boolean;
}

export interface ScopeAnchor { location: string; domain: string; descriptor: string; }

export interface ConfirmedScope {
  type: "brand" | "category" | "product" | "entity";
  name: string;
  anchor?: ScopeAnchor | null;
}

export interface ScopePickerHandle {
  /** Confirm the currently highlighted row (the pill's primary button calls this). */
  confirmActive: () => void;
}

interface Row {
  key: string;
  label: string;
  sub: string;
  isDefault?: boolean;
  drill?: boolean;      // "A specific product" row
  chain?: boolean;      // entity row that leads to its own scope page
  entityName?: string;  // set on entity rows
  anchor?: ScopeAnchor | null;
  scope: ConfirmedScope | null; // null for the drill row
}

function sentenceCase(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

type Page = "entities" | "scope" | "products" | "unknown";

export const ScopePicker = forwardRef<ScopePickerHandle, {
  brand: string;
  candidates: ResolvedCandidate[];
  anchorEl?: HTMLElement | null;
  onConfirm: (scope: ConfirmedScope) => void;
  onWholeBrand: () => void;
  onHighlight?: (buttonLabel: string) => void;
  /** Close WITHOUT running anything (outside click / user edits the brand). */
  onDismiss?: () => void;
  /** Fetch the categories/products belonging to ONE disambiguated
   *  organization (a second scoped resolve). Each entity gets its own scope
   *  tree: Yamaha Motor gets motorcycles, Yamaha Corporation gets pianos. */
  resolveScopes?: (entityName: string) => Promise<ResolvedCandidate[]>;
  /** The raw typed text when the resolver corrected the spelling. Renders a
   *  pinned keep-what-I-typed row so launch brands and novel names are never
   *  silently renamed to a famous lookalike. */
  keepTypedName?: string | null;
  /** True when the resolver found essentially nothing: open straight to the
   *  new-brand page ("the AIs don't know much about this yet, add a website"). */
  unknown?: boolean;
}>(function ScopePicker({ brand, candidates, anchorEl, onConfirm, onWholeBrand, onHighlight, onDismiss, resolveScopes, keepTypedName, unknown }, ref) {
  const entities = candidates.filter((c) => c.kind === "entity");
  const entityMode = entities.length >= 2 || entities.some((e) => e.known);

  const [page, setPage] = useState<Page>(unknown ? "unknown" : entityMode ? "entities" : "scope");
  const [website, setWebsite] = useState("");
  const [pendingAnchor, setPendingAnchor] = useState<ScopeAnchor | null>(null);
  // The disambiguated organization whose scope tree we're showing, and that
  // tree's candidates (from the per-entity resolve). In non-entity mode these
  // stay null and the initial candidates are used directly.
  const [scopeEntityName, setScopeEntityName] = useState<string | null>(null);
  const [scopeCandidates, setScopeCandidates] = useState<ResolvedCandidate[] | null>(null);
  const [loadingEntity, setLoadingEntity] = useState<string | null>(null);
  const loadSeq = useRef(0);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const scopeName = scopeEntityName || brand;
  const activeCandidates = scopeCandidates ?? candidates;
  const categories = activeCandidates.filter((c) => c.kind === "category");
  const products = activeCandidates.filter((c) => c.kind === "product");

  // ── Row builders ─────────────────────────────────────────────────────────
  const entityRows: Row[] = useMemo(() => {
    const ordered = [...entities].sort((a, b) => Number(!!b.known) - Number(!!a.known));
    return ordered.slice(0, 5).map((e, i) => {
      const anchor: ScopeAnchor = {
        location: e.location || "", domain: e.domain || "", descriptor: e.descriptor || "",
      };
      const subParts = [e.descriptor, e.location].filter(Boolean);
      return {
        key: `ent:${e.name}:${e.location || e.domain}`,
        label: e.location ? `${e.name} (${e.location})` : e.name,
        sub: subParts.join(" · ") || (e.domain || ""),
        isDefault: !!e.known || i === 0,
        // Every entity chains: its own scope tree is fetched on selection.
        chain: !!resolveScopes,
        entityName: e.name,
        anchor,
        scope: { type: "entity", name: e.name, anchor },
      };
    });
  }, [entities, resolveScopes]);

  const scopeRows: Row[] = useMemo(() => {
    const list: Row[] = [{
      key: "all",
      label: `All of ${scopeName}`,
      sub: "Ranked against full-line competitors across everything they make.",
      isDefault: true,
      scope: { type: "brand", name: scopeName },
    }];
    for (const c of categories.slice(0, 4)) {
      list.push({
        key: `cat:${c.name}`,
        label: sentenceCase(c.name),
        sub: `Ranked against other brands' ${c.name.toLowerCase()}.`,
        scope: { type: "category", name: c.name },
      });
    }
    return list;
  }, [scopeName, categories]);

  const productRows: Row[] = useMemo(() =>
    products.map((p) => ({
      key: `prod:${p.name}`,
      label: p.name,
      sub: sentenceCase(p.descriptor || ""),
      scope: { type: "product" as const, name: p.name },
    })), [products]);

  const drillRow: Row | null = products.length > 0 ? {
    key: "drill",
    label: "A specific product",
    sub: "Focus the analysis on one product",
    drill: true,
    scope: null,
  } : null;

  // Pinned on the FIRST page only, never filtered out: the explicit
  // keep-what-I-typed row shown after a spelling correction.
  const keepTypedRow: Row | null = keepTypedName ? {
    key: "keep-typed",
    label: `Keep "${keepTypedName}"`,
    sub: "Not a misspelling. Analyze the name exactly as typed.",
    scope: { type: "brand", name: keepTypedName },
  } : null;

  // Pinned last on the entities page, never filtered out: the escape for
  // when NONE of the guessed organizations is the right one. Leads to the
  // website page so the correct brand gets pinned by its domain.
  const differentRow: Row = {
    key: "different",
    label: `A different ${brand}`,
    sub: "None of these. Add its website so we measure the right one.",
    drill: false,
    chain: false,
    scope: null,
  };
  const firstPage: Page = entityMode ? "entities" : "scope";

  const filtered: Row[] = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const match = (r: Row) => !q || r.label.toLowerCase().includes(q) || r.sub.toLowerCase().includes(q);
    if (page === "products") return productRows.filter(match);
    if (page === "entities") {
      const base = entityRows.filter(match);
      const tail = keepTypedRow ? [keepTypedRow, differentRow] : [differentRow];
      return [...base, ...tail];
    }
    const base = scopeRows.filter(match);
    const withDrill = drillRow ? [...base, drillRow] : base; // drill pinned, never filtered out
    return (keepTypedRow && page === firstPage) ? [...withDrill, keepTypedRow] : withDrill;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, entityRows, scopeRows, productRows, drillRow, keepTypedRow, differentRow, firstPage, filter]);

  useEffect(() => { setActive(0); }, [filter, page]);
  useEffect(() => { inputRef.current?.focus(); }, [page]);

  const buttonLabelFor = (row: Row | undefined): string => {
    if (loadingEntity) return "Checking...";
    if (page === "unknown") return website.trim() ? `Analyze ${scopeName}` : "Analyze anyway";
    if (!row) return `Analyze ${scopeName}`;
    if (row.key === "different") return "Add its website";
    if (row.drill) return "Choose a product";
    if (row.chain) return `Continue with ${row.entityName || row.label}`;
    if (!row.scope) return `Analyze ${scopeName}`;
    if (row.scope.type === "brand") return `Analyze all of ${scopeName}`;
    if (row.scope.type === "product") return `Analyze the ${row.scope.name}`;
    return `Analyze ${row.scope.name}`;
  };

  useEffect(() => {
    onHighlight?.(buttonLabelFor(filtered[active]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, filtered, brand, page, website, loadingEntity]);

  const go = (p: Page) => { setPage(p); setFilter(""); setActive(0); };
  const popUp = () => {
    if (loadingEntity) { loadSeq.current += 1; setLoadingEntity(null); return; }
    if (page === "products") { go("scope"); return; }
    if (page === "unknown" && !unknown) {
      // Reached via the keep-typed row: back returns to the first page.
      setWebsite(""); setScopeEntityName(null);
      go(entityMode ? "entities" : "scope"); return;
    }
    if (page === "scope" && entityMode) {
      setPendingAnchor(null); setScopeEntityName(null); setScopeCandidates(null);
      go("entities"); return;
    }
  };

  const chainInto = (row: Row) => {
    // Fetch THIS organization's own categories/products, then show its scope
    // page. If it has none (or the lookup fails), confirm the entity directly:
    // never block, never borrow another entity's products.
    const seq = ++loadSeq.current;
    setLoadingEntity(row.entityName || row.label);
    (resolveScopes ? resolveScopes(row.entityName || brand) : Promise.resolve([]))
      .catch(() => [] as ResolvedCandidate[])
      .then((cands) => {
        if (loadSeq.current !== seq) return; // user backed out meanwhile
        setLoadingEntity(null);
        const scoped = (cands || []).filter((c) => c.kind === "category" || c.kind === "product");
        if (scoped.length === 0) {
          onConfirm({ type: "entity", name: row.entityName || brand, anchor: row.anchor || null });
          return;
        }
        setPendingAnchor(row.anchor || null);
        setScopeEntityName(row.entityName || brand);
        setScopeCandidates(scoped);
        go("scope");
      });
  };

  const confirmUnknown = () => {
    const raw = website.trim();
    if (!raw) { onWholeBrand(); return; }
    // Normalize a pasted URL down to its domain.
    const domain = raw.replace(/^https?:\/\//i, "").replace(/\/.*$/, "").trim();
    onConfirm({
      type: "entity",
      name: scopeName,
      anchor: { domain, location: "", descriptor: "" },
    });
  };

  const confirmRow = (row: Row | undefined) => {
    if (page === "unknown") { confirmUnknown(); return; }
    if (!row || loadingEntity) { if (!row) onWholeBrand(); return; }
    if (row.drill) { go("products"); return; }
    if (row.key === "keep-typed" && keepTypedName) {
      // A kept-as-typed name is by definition one the AIs may not know:
      // land on the new-brand page so a website can anchor it.
      setScopeEntityName(keepTypedName);
      setWebsite("");
      go("unknown");
      return;
    }
    if (row.key === "different") {
      // None of the guessed organizations is right: pin the real one by
      // its website.
      setScopeEntityName(null);
      setWebsite("");
      go("unknown");
      return;
    }
    if (row.chain) { chainInto(row); return; }
    if (!row.scope) { onWholeBrand(); return; }
    // Attach the pending entity anchor so identity and scope travel together:
    // "Yamaha Motor (yamaha-motor.com), scoped to Motorcycles".
    onConfirm({ ...row.scope, anchor: row.scope.anchor ?? pendingAnchor });
  };

  useImperativeHandle(ref, () => ({
    confirmActive: () => confirmRow(filtered[active]),
  }));

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (page === "unknown") {
      if (e.key === "Enter") { e.preventDefault(); confirmUnknown(); }
      else if (e.key === "Escape") { e.preventDefault(); onWholeBrand(); }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); confirmRow(filtered[active]); }
    else if (e.key === "Backspace" && filter === "" && page !== "entities" && !(page === "scope" && !entityMode)) {
      e.preventDefault(); popUp();
    }
    else if (e.key === "Escape") {
      e.preventDefault();
      if (page === "products") { popUp(); return; }
      if (page === "scope") {
        if (entityMode) { popUp(); return; }
        // Category-only picker: Escape means whole brand and go, a real
        // confirmation (full-line ranking).
        onConfirm({ type: "brand", name: brand, anchor: pendingAnchor });
        return;
      }
      // Entities page: no meaningful whole-brand exists; run as typed, unscoped.
      onWholeBrand();
    }
  };

  const prompt =
    page === "unknown" ? `The AIs don't know much about ${scopeName} yet` :
    page === "entities" ? `Which ${brand} do you mean?` :
    page === "products" ? `${scopeName} products` :
    entityMode ? `Which part of ${scopeName}?` : `Which ${scopeName} should we analyze?`;
  const headerLabel =
    page === "unknown" ? "PIN THE BRAND" :
    page === "entities" ? "MULTIPLE MATCHES" :
    page === "products" ? "PICK ONE" : "MULTIPLE CATEGORIES";
  const showBack = page === "products" || (page === "scope" && entityMode) || (page === "unknown" && !unknown);
  const escHint =
    page === "unknown" ? "Analyze anyway" :
    page === "products" ? "Back" :
    page === "scope" ? (entityMode ? "Back" : "Whole brand") : "Run as typed";
  const kbd = (t: string) => (
    <span style={{ padding: "1px 5px", border: "1px solid var(--border)", borderRadius: 4, background: "var(--background)", fontFamily: "var(--font-mono)", fontSize: 10 }}>{t}</span>
  );

  // ── Dismiss on outside click ───────────────────────────────────────────
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!onDismiss) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t)) return;
      if (anchorEl?.contains(t)) return;
      onDismiss();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onDismiss, anchorEl]);

  // ── Body-portal positioning ────────────────────────────────────────────
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  useLayoutEffect(() => {
    if (!anchorEl) { setPos(null); return; }
    const update = () => {
      const r = anchorEl.getBoundingClientRect();
      setPos({ top: r.bottom + 8, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [anchorEl]);

  const positioning: React.CSSProperties = pos
    ? { position: "fixed", top: pos.top, left: pos.left, width: pos.width, zIndex: 60 }
    : { position: "absolute", left: 0, right: 0, top: "calc(100% + 8px)", zIndex: 35 };

  const popover = (
    <div
      ref={rootRef}
      role="listbox"
      aria-label={prompt}
      style={{
        ...positioning,
        background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14,
        boxShadow: "var(--elevation-lg)", overflow: "hidden",
        animation: "scopePickerIn 160ms var(--ease-standard)",
      }}
    >
      <style>{`@keyframes scopePickerIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } } @media (prefers-reduced-motion: reduce) { [role="listbox"] { animation: none !important; } }`}</style>

      {/* Header bar */}
      <div className="flex items-center" style={{ justifyContent: "space-between", padding: "11px 16px", borderBottom: "1px solid var(--border)", background: "var(--muted)" }}>
        <span className="flex items-center" style={{ gap: 10 }}>
          {showBack && (
            <button
              onClick={popUp}
              className="inline-flex items-center"
              style={{ gap: 3, padding: "2px 8px", borderRadius: 6, border: "1px solid var(--border)", background: "var(--background)", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--foreground)" }}
            >
              <ChevronLeft style={{ width: 13, height: 13 }} /> Back
            </button>
          )}
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>{prompt}</span>
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "var(--muted-foreground)" }}>{headerLabel}</span>
      </div>

      {/* Filter field (list pages) / website field (new-brand page) */}
      {page === "unknown" ? (
        <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
          <div style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.5, marginBottom: 10 }}>
            Add the brand's website so the measurement is pinned to the right
            one from the start.
          </div>
          <div className="flex items-center" style={{ gap: 10, padding: "8px 12px", border: "1px solid var(--border)", borderRadius: 8, background: "var(--background)" }}>
            <Search style={{ width: 15, height: 15, color: "var(--muted-foreground)", flexShrink: 0 }} />
            <input
              ref={inputRef}
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="yourbrand.com"
              style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 14, fontFamily: "inherit", color: "var(--foreground)" }}
            />
          </div>
        </div>
      ) : (
      <div className="flex items-center" style={{ gap: 10, padding: "9px 16px", borderBottom: "1px solid var(--border)" }}>
        <Search style={{ width: 15, height: 15, color: "var(--muted-foreground)", flexShrink: 0 }} />
        <input
          ref={inputRef}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={
            page === "products" ? `Filter ${brand} products` :
            page === "entities" ? "Filter matches" : "Filter categories"
          }
          style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 14, fontFamily: "inherit", color: "var(--foreground)" }}
        />
      </div>
      )}

      {/* Rows */}
      <div style={{ maxHeight: 320, overflowY: "auto" }}>
        {page === "unknown" && (
          <div style={{ padding: "12px 16px", fontSize: 12.5, color: "var(--muted-foreground)", lineHeight: 1.5 }}>
            Press Enter to analyze with the website, or Escape to analyze the
            name on its own.
          </div>
        )}
        {page !== "unknown" && loadingEntity && (
          <div style={{ padding: 16, fontSize: 13, color: "var(--muted-foreground)" }}>
            Checking {loadingEntity}...
          </div>
        )}
        {page !== "unknown" && !loadingEntity && filtered.length === 0 && (
          <div style={{ padding: 16, fontSize: 13, color: "var(--muted-foreground)" }}>
            No matches for "{filter}"
          </div>
        )}
        {page !== "unknown" && !loadingEntity && filtered.map((row, i) => {
          const isActive = i === active;
          return (
            <div
              key={row.key}
              role="option"
              aria-selected={isActive}
              onMouseEnter={() => setActive(i)}
              onClick={() => confirmRow(row)}
              className="flex items-center"
              style={{
                gap: 12, padding: "11px 16px", cursor: "pointer", minHeight: 44,
                borderBottom: "1px solid var(--border)",
                background: isActive ? "var(--alpha-teal-5)" : "transparent",
                boxShadow: isActive ? "inset 2px 0 0 var(--accent)" : "none",
                transition: "background 120ms var(--ease-standard)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="flex items-center" style={{ gap: 8 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--tx-strong)" }}>{row.label}</span>
                  {row.isDefault && (
                    <span style={{ padding: "1px 7px", borderRadius: 5, fontSize: 10.5, fontWeight: 600, background: "var(--alpha-teal-10)", color: "var(--alpha-teal)" }}>Default</span>
                  )}
                </div>
                {row.sub && (
                  <div style={{ fontSize: 12.5, color: "var(--muted-foreground)", lineHeight: 1.4, marginTop: 2 }}>{row.sub}</div>
                )}
              </div>
              {(row.drill || row.chain || row.key === "different") ? (
                <ChevronRight style={{ width: 16, height: 16, color: "var(--muted-foreground)", flexShrink: 0 }} />
              ) : isActive ? (
                <span className="inline-flex items-center justify-center" style={{ minWidth: 20, height: 20, border: "1px solid var(--alpha-teal-50)", background: "var(--alpha-teal-10)", color: "var(--alpha-teal)", borderRadius: 5, flexShrink: 0 }}>
                  <CornerDownLeft style={{ width: 12, height: 12 }} />
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Footer hints */}
      <div className="flex items-center" style={{ gap: 14, padding: "9px 16px", borderTop: "1px solid var(--border)", background: "var(--muted)", fontSize: 11, color: "var(--muted-foreground)" }}>
        <span className="flex items-center" style={{ gap: 5 }}>{kbd("↑↓")} Move</span>
        <span className="flex items-center" style={{ gap: 5 }}>{kbd("↵")} {page === "entities" ? "Select" : "Analyze"}</span>
        <span className="flex items-center" style={{ gap: 5 }}>{kbd("Esc")} {escHint}</span>
      </div>
    </div>
  );

  return pos ? createPortal(popover, document.body) : popover;
});
